import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { GetCookie } from "@shared_components/functions/cookies";

type GearstudioUser = {
  id: string;
  email: string;
};

type SessionResponse = {
  logged_in: boolean;
  user?: GearstudioUser;
};

type AuthResult = {
  ok: boolean;
  message?: string;
};

type SignupPayload = {
  email: string;
  password: string;
  password2: string;
};

type ResetPasswordPayload = {
  uid: string;
  token: string;
  password: string;
  password2: string;
};

type GearstudioAuthContextType = {
  loading: boolean;
  loggedIn: boolean;
  user: GearstudioUser | null;
  login: (email: string, password: string) => Promise<AuthResult>;
  signup: (payload: SignupPayload) => Promise<AuthResult>;
  requestPasswordReset: (email: string) => Promise<AuthResult>;
  resetPassword: (payload: ResetPasswordPayload) => Promise<AuthResult>;
  loginWithGoogle: (credential: string) => Promise<AuthResult>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
};

const GearstudioAuthContext = createContext<GearstudioAuthContextType | undefined>(undefined);

//These are under backend/gearstudio
const SESSION_PATH = "/auth/session/";
const LOGIN_PATH = "/auth/login/";
const SIGNUP_PATH = "/auth/signup/";
const FORGOT_PASSWORD_PATH = "/auth/forgot-password/";
const RESET_PASSWORD_PATH = "/auth/reset-password/";
const GOOGLE_AUTH_PATH = "/auth/google-auth/";
const LOGOUT_PATH = "/auth/logout/";
export const GEARSTUDIO_GOOGLE_CLIENT_ID = "15234967473-kqp8n4p8vqqkmfv9rde9498qlc2dudug.apps.googleusercontent.com";

export const GearstudioAuthProvider = ({ children }: { children: ReactNode }) => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<GearstudioUser | null>(null);

  const applyAuthResponse = (data: SessionResponse) => {
    if (data.logged_in && data.user?.email) {
      setUser(data.user);
    } else {
      setUser(null);
    }
  };

  const refreshSession = async () => {
    try {
      const response = await fetch(SESSION_PATH, {
        method: "GET",
        credentials: "same-origin",
      });

      if (!response.ok) {
        setUser(null);
        return;
      }

      const data: SessionResponse = await response.json();
      applyAuthResponse(data);
    } catch (error) {
      setUser(null);
    }
  };

  useEffect(() => {
    const initSession = async () => {
      await refreshSession();
      setLoading(false);
    };

    initSession();
  }, []);

  const login = async (email: string, password: string): Promise<AuthResult> => {
    try {
      const response = await fetch(LOGIN_PATH, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": GetCookie() || "",
        },
        body: JSON.stringify({
          email,
          password,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        return { ok: false, message: data.detail || "Login failed." };
      }

      applyAuthResponse(data);

      return { ok: true };
    } catch (error) {
      console.log("Login error:", error);
      return { ok: false, message: "Login failed. Please try again." };
    }
  };

  const signup = async (payload: SignupPayload): Promise<AuthResult> => {
    try {
      const response = await fetch(SIGNUP_PATH, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": GetCookie() || "",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok) {
        return { ok: false, message: data.detail || "Signup failed." };
      }

      applyAuthResponse(data);
      return { ok: true };
    } catch (error) {
      console.log("Signup error:", error);
      return { ok: false, message: "Signup failed. Please try again." };
    }
  };

  const requestPasswordReset = async (email: string): Promise<AuthResult> => {
    try {
      const response = await fetch(FORGOT_PASSWORD_PATH, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": GetCookie() || "",
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();
      if (!response.ok) {
        return { ok: false, message: data.detail || "Could not send reset email." };
      }

      return { ok: true, message: data.detail };
    } catch (error) {
      console.log("Forgot password error:", error);
      return { ok: false, message: "Could not send reset email. Please try again." };
    }
  };

  const resetPassword = async (payload: ResetPasswordPayload): Promise<AuthResult> => {
    try {
      const response = await fetch(RESET_PASSWORD_PATH, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": GetCookie() || "",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok) {
        return { ok: false, message: data.detail || "Could not reset password." };
      }

      return { ok: true, message: data.detail };
    } catch (error) {
      console.log("Reset password error:", error);
      return { ok: false, message: "Could not reset password. Please try again." };
    }
  };

  const loginWithGoogle = async (credential: string): Promise<AuthResult> => {
    try {
      const response = await fetch(GOOGLE_AUTH_PATH, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": GetCookie() || "",
        },
        body: JSON.stringify({ credential }),
      });

      const data = await response.json();
      if (!response.ok) {
        return { ok: false, message: data.detail || "Google sign-in failed." };
      }

      applyAuthResponse(data);
      return { ok: true };
    } catch (error) {
      console.log("Google login error:", error);
      return { ok: false, message: "Google sign-in failed. Please try again." };
    }
  };

  const logout = async () => {
    try {
      await fetch(LOGOUT_PATH, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "X-CSRFToken": GetCookie() || "",
        },
      });
    } finally {
      setUser(null);
    }
  };

  const value = useMemo(
    () => ({
      loading,
      loggedIn: !!user,
      user,
      login,
      signup,
      requestPasswordReset,
      resetPassword,
      loginWithGoogle,
      logout,
      refreshSession,
    }),
    [loading, user],
  );

  return <GearstudioAuthContext.Provider value={value}>{children}</GearstudioAuthContext.Provider>;
};

export const useGearstudioAuth = () => {
  const context = useContext(GearstudioAuthContext);
  if (!context) {
    throw new Error("useGearstudioAuth must be used within GearstudioAuthProvider.");
  }
  return context;
};
